// @flow
import url from 'node:url';

import * as React from 'react';

import get from 'lodash/get';

import { serializeQuery } from '@gandi/app-runner';
import type { FetchResponse } from '@gandi/resource/redux';
import type { TranslatorProps } from '@gandi/translate';
import { Spinner, Switch } from '@gandi/ui';
import { Action, Divider } from '@gandi/ui/deprecated';

import {
  SubmissionError,
  Fieldset,
  Form,
  InputTextField,
  TextAreaField,
  type FormComponentProps,
} from 'gandi.react-form';
// import type { ProductId, ProductPayload } from '@gandi/cart';

import PasswordChecker from '../../../../components/PasswordChecker';
import type { WithRouterProps } from '../../../../types';
import type { PresenterProps as ParentProps } from '../../Presenter';
import { mapErrorsToFields } from '../Form';

export type PresenterStateProps = {
  cartIsWorking: boolean,
  isPasswordSet: boolean,
  username: string,
  passwordService: string,
};

export type PresenterDispatchProps = {
  POST: (
    string,
    { [string]: mixed, ... },
    // $FlowFixMe[incompatible-call]
    { [string]: mixed, ... },
  ) => Promise<any>,
  updateCartProduct: any,
};

export type PresenterOwnProps = {
  form: FormComponentProps,
  translator: TranslatorProps,
};

type PresenterState = {
  isSettingsOpen: boolean,
  suggestions: ?Array<string>,
};

export type PresenterProps = {
  ...PresenterStateProps,
  ...PresenterDispatchProps,
  ...PresenterOwnProps,
  ...$Exact<ParentProps>,
  ...WithRouterProps<{ cartId: string }>,
};

const startWithAlpha = /^[a-zA-Z]/;
const startWithAlphaNum = /^[a-zA-Z0-9]/;
const endWithAlphaNum = /[a-zA-Z0-9]$/;
const specialChar = /([^-.a-zA-Z0-9])/g;

export default class Presenter extends React.Component<
  PresenterProps,
  PresenterState,
> {
  state: PresenterState = {
    isSettingsOpen:
      !!this.props.form.initialValues.sshkey ||
      !!this.props.form.initialValues.username,
    suggestions: undefined,
  };

  componentDidMount() {
    this.props.form.change('password', '');
  }

  UNSAFE_componentWillReceiveProps(nextProps: PresenterProps) {
    if (this.props.product && !nextProps.product) {
      this.props.navigate('/cloud/create', { replace: true });
    }
  }

  validateHostname: (value: string) => string = (value: string) => {
    if (typeof value !== 'string' || !value) {
      return this.props.translator.__('Please enter a valid hostname.');
    }

    if (!value.match(startWithAlphaNum)) {
      return this.props.translator.__(
        'The server name must start with a letter or a number.',
      );
    }

    if (!value.match(endWithAlphaNum)) {
      return this.props.translator.__(
        'The server name must end with a letter or a number.',
      );
    }

    if (value.length > 64) {
      return this.props.translator.__(
        'The server name must be less than 64 characters.',
      );
    }

    if (value === 'localhost') {
      return this.props.translator.__(
        'Find a specific name rather than localhost.',
      );
    }

    const result = value.match(specialChar);
    if (result) {
      return this.props.translator.__(
        "'%(characters)s' not allowed. Please, use dash, dots, numbers and " +
          'letters',
        {
          characters: [...new Set(result)].join("', '"), // deduplicate result
        },
      );
    }

    return '';
  };

  validateUsername: (value: string) => string = (value: string) => {
    if (!value) {
      return '';
    }

    if (typeof value !== 'string') {
      return this.props.translator.__('Please enter a valid account name.');
    }

    if (!value.match(startWithAlpha)) {
      return this.props.translator.__('Must start with a letter.');
    }

    if (!value.match(endWithAlphaNum)) {
      return this.props.translator.__('Must end with a letter or a number.');
    }

    const result = value.match(specialChar);
    if (result) {
      return this.props.translator.__(
        "'%(characters)s' not allowed. Please, use dash, dots, numbers and " +
          'letters',
        {
          characters: [...new Set(result)].join("', '"), // deduplicate result
        },
      );
    }

    return '';
  };

  handleScoreChange: (score: number) => void = (score: number) => {
    let suggestions;

    if (score > 2) {
      suggestions = [
        this.props.translator.__(
          'Used to log into the server\'s superuser account ("admin" by default).',
        ),
      ];
    }

    this.setState({
      suggestions,
    });
  };

  handleSubmit: (values: { [string]: mixed, ... }) => Promise<mixed> =
    async (values: { [string]: mixed, ... }) => {
      if (!this.props.product) {
        return Promise.reject();
      }

      let hash;
      const product = this.props.product;

      try {
        const hashResponse = await this.props.POST(
          url.resolve(this.props.passwordService, '/hash'),
          { password: values.password, user_inputs: [values.name || ''] },
          {
            params: {
              target: 'cloud',
            },
            on200: (response: FetchResponse<>) => Promise.resolve(response),
            not200: (response: FetchResponse<>) => {
              // $FlowFixMe[class-object-subtyping]
              const error: Error & { [string]: mixed, ... } = new Error(
                'Unable to hash password',
              );
              error.response = response;

              throw error;
            },
          },
        );

        hash = hashResponse.data.hash;
      } catch (err) {
        if (get(err, 'response.status') === 403) {
          throw new SubmissionError({
            password: this.props.translator.__(
              "The submitted password's strength is too low, please use a stronger password.",
            ),
          });
        }

        throw new SubmissionError({
          _error: this.props.translator.__(
            'Unable to save your server settings at this time.',
          ),
        });
      }

      // do not send empty string for username and sshkey
      const username = !values.username ? 'admin' : values.username;
      const sshkey = !values.sshkey ? undefined : values.sshkey;

      const payload = {
        ...product.payload,
        extra: {
          ...product.payload.extra,
          name: values.name,
          username,
        },
        server: {
          ...product.payload.server,
          hostname: values.name,
          authentication: {
            password: hash,
            username,
          },
        },
      };

      if (sshkey) {
        // $FlowFixMe[prop-missing]
        payload.extra.sshkey = sshkey;
        // $FlowFixMe[prop-missing]
        payload.server.authentication.ssh_keys = [sshkey];
      }

      return this.props
        .updateCartProduct({ productId: product.id, payload })
        .then(response => {
          const errors = mapErrorsToFields(get(response, 'data.errors', []), [
            'name',
            'password',
            'username',
            'sshkey',
          ]);

          if (errors) {
            throw new SubmissionError(errors);
          } else {
            this.props.navigate({
              pathname: `/cloud/create/confirmation/${this.props.params.cartId}/${product.id}`,
              search: this.props.isFirstServer
                ? undefined
                : serializeQuery({
                    amount: 0,
                  }),
            });
          }
        });
    };

  handleSwitchChange: (evt: SyntheticInputEvent<>) => void = (
    evt: SyntheticInputEvent<>,
  ) => {
    const isSettingsOpen = evt.target.checked;

    this.setState({
      isSettingsOpen,
    });

    if (!isSettingsOpen) {
      this.props.form.change('sshkey', '');
    } else {
      this.props.form.change('username', 'admin');
    }
  };

  render(): React.Node {
    const {
      translator: { __ },
    } = this.props;

    if (!this.props.product) {
      return <Spinner centered />;
    }

    const product = this.props.product;

    const usernameHelp = !this.props.username
      ? __('"admin" will be used if submitted as empty.')
      : __('The superuser account password will be associated with this name.');

    return (
      <Form
        role="form"
        method="post"
        id="cloud-create-server"
        className="form-stacked form-create"
        onSubmit={this.props.form.handleSubmit(this.handleSubmit)}
      >
        <InputTextField
          help={__(
            'Should start and end with a letter or a number, be less ' +
              "than 64 characters. '-' and '.' are the only special " +
              "characters allowed. It can not be 'localhost'",
          )}
          label={__('Server Name')}
          name="name"
          placeholder={__(
            'Name of your server (example: dev01, prod, mytest...)',
          )}
          required
          validate={this.validateHostname}
        />
        <PasswordChecker
          autoComplete="new-password"
          inputs={[String(this.props.formValues.name) || '']}
          label={__('Superuser Account Password')}
          name="password"
          onScoreChange={this.handleScoreChange}
          placeholder={__('Enter your password')}
          required
          suggestions={this.state.suggestions}
        />
        <Fieldset>
          <Switch
            checked={this.state.isSettingsOpen}
            name="advanced-settings"
            onChange={this.handleSwitchChange}
          >
            {__('Advanced settings')}
          </Switch>
        </Fieldset>
        {this.state.isSettingsOpen && (
          <div>
            <InputTextField
              help={usernameHelp}
              label={__('Superuser Account Name')}
              name="username"
              validate={this.validateUsername}
            />
            <TextAreaField
              help={__(
                'Will be associated with both the "root" and superuser account.',
              )}
              label={__('SSH Key')}
              name="sshkey"
            />
          </div>
        )}
        <Divider direction="columnToRow" spread="equal">
          <Action
            disabled={this.props.form.dirty && !this.props.form.valid}
            icon="arrowLeft"
            to={`/cloud/create/server/${this.props.params.cartId}/${product.id}`}
            variant="block"
          >
            {__('Previous')}
          </Action>
          <Action
            type="submit"
            variant="block"
            icon="arrowRight"
            loading={this.props.cartIsWorking}
            form="cloud-create-server"
            disabled={!this.props.form.valid || !this.props.isPasswordSet}
          >
            {__('Next')}
          </Action>
        </Divider>
      </Form>
    );
  }
}
